import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { majorContractsService } from "@shared/services/majorContracts";
import { notifySuccess, notifyError } from "@config/tostifyConfig/tostifyConfig";
import LoadingButton from "@shared/components/LoadingButton";

export default function MajorContractTranslation({ onClose, record, onRefresh }) {
    const { t } = useTranslation();
    const modalRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { register, handleSubmit, formState: { errors }, reset, trigger, setValue, watch } = useForm();

    const watchedValues = watch();

    useEffect(() => {
        const fetchTranslations = async () => {
            if (record) {
                try {
                    setLoading(true);
                    setError(null);
                    
                    setValue('reasonKt', record.reasonKt || '');
                    setValue('description', record.description || '');
                    setValue('termDescription', record.termDescription || '');
                    
                    const translationData = await majorContractsService.getTranslation(record.id);
                    if (translationData && translationData.length > 0) {
                        const translation = translationData[0];
                        setValue('reasonKtTranslated', translation.translatedReasonKt || '');
                        setValue('descriptionTranslated', translation.translatedDescription || '');
                        setValue('termDescriptionTranslated', translation.translatedTermDescription || '');
                    } else {
                        setValue('reasonKtTranslated', '');
                        setValue('descriptionTranslated', '');
                        setValue('termDescriptionTranslated', '');
                    }
                } catch (err) {
                    console.error('Error fetching translations:', err);
                    setError('Failed to load translations');
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchTranslations();
    }, [record, setValue]);

    const submitForm = async (data) => {
        try {
            setLoading(true);
            const translationData = {
                id: record.id,
                langCode: "en_US",
                reasonKt: data.reasonKtTranslated,
                description: data.descriptionTranslated,
                termDescription: data.termDescriptionTranslated
            };

            await majorContractsService.saveTranslation(translationData);
            notifySuccess(t('common.translationSuccessfully'));
            if (onRefresh) {
                await onRefresh();
            }
            onClose();
        } catch (error) {
            console.error('Error saving translation:', error);
            notifyError(t('common.errorMessage'));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="modal show" tabIndex="-1">
                <div className="modal-dialog modal-confirm" ref={modalRef}>
                    <div className="modal-content">
                        <div className="modal-body">
                            <p>Loading translations...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {t('staff.majorContracts.translations.translations')}
                        </h4>
                        <form id="translationsForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="form-sections">
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('staff.majorContracts.translations.reasonKt')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            disabled
                                            {...register('reasonKt')}
                                            value={watchedValues.reasonKt || ''}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('staff.majorContracts.translations.description')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            disabled
                                            {...register('description')}
                                            value={watchedValues.description || ''}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('staff.majorContracts.translations.termDescription')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            disabled
                                            {...register('termDescription')}
                                            value={watchedValues.termDescription || ''}
                                        />
                                    </div>
                                </div>
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">{t('staff.majorContracts.translations.reasonKtTranslated')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register('reasonKtTranslated')}
                                            value={watchedValues.reasonKtTranslated || ''}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('staff.majorContracts.translations.descriptionTranslated')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register('descriptionTranslated')}
                                            value={watchedValues.descriptionTranslated || ''}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('staff.majorContracts.translations.termDescriptionTranslated')}</span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            {...register('termDescriptionTranslated')}
                                            value={watchedValues.termDescriptionTranslated || ''}
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <LoadingButton type="submit" form="translationsForm" className="btn btn-primary" loading={loading} disabled={loading}>
                            {t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

